import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from './useAuth';

export interface ActionItem {
  tool: string;
  target: string;
  status: 'running' | 'success' | 'error';
  iteration?: number;
}

export interface ChatSession {
  id: string;
  prompt: string;
  state: string;
  pipelineState: string;
  planStream: string;
  codeStream: string;
  finalResult: string;
  sandboxLogs?: string;
  reviewerFeedback?: string;
  healingMessage?: string;
  error?: string;
  swarm_session_id?: string;
  actions?: ActionItem[];
}

export interface Conversation {
  id: string;
  title: string;
  projectId: string;
  projectRules: string;
  messages: ChatSession[];
  created_at: string;
  updated_at?: string;
}

interface ChatHistoryCtx {
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;
  conversations: Conversation[];
  activeId: string | null;
  projects: any[];
  localWorkspaces: string[];
  loadingProjects: boolean;
  createConversation: (projectId: string, prompt?: string) => string;
  deleteConversation: (id: string) => void;
  updateMessages: (id: string, messages: ChatSession[], rules?: string, title?: string) => void;
  setActiveId: (id: string | null) => void;
  loadProjects: () => void;
  deleteConversationsByProject: (projectId: string) => void;
}

const ChatHistoryContext = createContext<ChatHistoryCtx | null>(null);

import { getLocalAgentConfig } from '../api/client';

async function executeLocalTool(method: string, args: any) {
  try {
    const agent = getLocalAgentConfig();
    if (!agent?.tunnel_url) throw new Error("No tunnel URL configured");
    const url = `${agent.tunnel_url}/execute_tool`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ method, args })
    });
    return await res.json();
  } catch (e) {
    throw e;
  }
}

export function ChatHistoryProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(() => {
    return localStorage.getItem('ericoder_active_project') || null;
  });
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [localWorkspaces, setLocalWorkspaces] = useState<string[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [shouldSync, setShouldSync] = useState(false);

  const setActiveProjectId = useCallback((id: string | null) => {
    setActiveProjectIdState(id);
    if (id) localStorage.setItem('ericoder_active_project', id);
    else localStorage.removeItem('ericoder_active_project');
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const stored = localStorage.getItem('eric_mec_workspaces');
        setLocalWorkspaces(stored ? JSON.parse(stored) : []);
      } catch (e) {}
    };
    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const loadProjects = useCallback(() => {
    setLoadingProjects(true);
    api.listProjects()
      .then((d: any) => setProjects(d.projects ?? []))
      .catch((e) => console.error('[useChatHistory] Failed to list projects:', e))
      .finally(() => setLoadingProjects(false));
  }, []);

  const activeProjectIdRef = useRef(activeProjectId);
  useEffect(() => { activeProjectIdRef.current = activeProjectId; }, [activeProjectId]);

  const skipFetchRef = useRef(false);

  const fetchConversationsForProject = useCallback(async (projId: string): Promise<Conversation[]> => {
    if (!isLoggedIn || !projId) return [];

    const isLocal = localWorkspaces.includes(projId) || projId.includes('\\') || projId.includes('/');
    
    if (isLocal) {
      try {
        const safeProjId = projId.replace(/'/g, "''"); // Escape single quotes for PowerShell
        const res: any = await executeLocalTool('run_command', { 
          CommandLine: `powershell -NoProfile -Command "try { Get-Content '${safeProjId}\.ericoder\chat_history.json' -Raw } catch { echo '[]' }"` 
        });
        const out = res.stdout || '[]';
        const parsed = JSON.parse(out);
        return parsed.conversations || parsed || [];
      } catch (e) {
        return [];
      }
    } else {
      try {
        const data: any = await api.getProjectChatHistory(projId);
        return data.conversations ?? [];
      } catch (err) {
        console.error('[useChatHistory] Failed to fetch cloud project history:', err);
        return [];
      }
    }
  }, [isLoggedIn, localWorkspaces]);

  useEffect(() => {
    if (!isLoggedIn || !activeProjectId) {
      setConversations([]);
      return;
    }

    let cancelled = false;
    fetchConversationsForProject(activeProjectId).then(loaded => {
      if (!cancelled) {
        setConversations(prev => {
          // Merge unsaved in-memory conversations for activeProjectId with loaded history
          const unsaved = prev.filter(c => c.projectId === activeProjectId && !loaded.some(l => l.id === c.id));
          return [...unsaved, ...loaded];
        });
      }
    });

    return () => { cancelled = true; };
  }, [activeProjectId, isLoggedIn, fetchConversationsForProject]);

  useEffect(() => {
    if (isLoggedIn) {
      loadProjects();
    } else {
      setProjects([]);
      setConversations([]);
      setActiveProjectId(null);
    }
  }, [isLoggedIn, loadProjects]);

  const createConversation = useCallback((projectId: string, prompt?: string) => {
    const id = `session_${Date.now()}`;
    const nowStr = new Date().toISOString();
    const newConv: Conversation = {
      id,
      title: prompt ? (prompt.length > 30 ? prompt.substring(0, 30) + '...' : prompt) : 'New Conversation',
      projectId,
      projectRules: '',
      messages: [],
      created_at: nowStr,
      updated_at: nowStr
    };
    
    // Synchronous state updates so UI updates immediately without async delay
    setConversations(prev => {
      const currentProjectConvs = prev.filter(c => c.projectId === projectId);
      return [newConv, ...currentProjectConvs];
    });
    
    setActiveProjectId(projectId);
    setActiveId(id);
    setShouldSync(true);

    return id;
  }, [setActiveProjectId]);

  const deleteConversation = useCallback((id: string) => {
    setConversations(prev => {
      const updated = prev.filter(c => c.id !== id);
      return updated;
    });
    setShouldSync(true);
    
    setActiveId(prevId => prevId === id ? null : prevId);
  }, []);

  const updateMessages = useCallback((id: string, messages: ChatSession[], rules?: string, title?: string) => {
    setConversations(prev => {
      let found = false;
      const nowStr = new Date().toISOString();
      const updated = prev.map(c => {
        if (c.id === id) {
          found = true;
          return {
            ...c,
            messages,
            projectRules: rules !== undefined ? rules : c.projectRules,
            title: title !== undefined ? title : (messages[0]?.prompt ? (messages[0].prompt.length > 30 ? messages[0].prompt.substring(0, 30) + '...' : messages[0].prompt) : c.title),
            updated_at: nowStr
          };
        }
        return c;
      });
      return updated;
    });
    setShouldSync(true);
  }, []);

  // ── Auto New Chat from EricMEC (SSO / URL parameter push) ──
  useEffect(() => {
    if (!isLoggedIn) return;
    try {
      const raw = sessionStorage.getItem('ericoder_auto_new_chat');
      if (raw) {
        sessionStorage.removeItem('ericoder_auto_new_chat');
        const data = JSON.parse(raw);
        if (data.project) {
          const targetWs = data.project;
          console.log('[AutoNewChat] Initializing new chat for pushed project from EricMEC:', targetWs);
          setActiveProjectId(targetWs);
          
          setLocalWorkspaces(prev => {
            if (!prev.includes(targetWs)) {
              const updated = [...prev, targetWs];
              localStorage.setItem('eric_mec_workspaces', JSON.stringify(updated));
              return updated;
            }
            return prev;
          });

          createConversation(targetWs, data.prompt);
          if (data.prompt) {
            sessionStorage.setItem('ericoder_initial_prompt', data.prompt);
          }
        }
      }
    } catch (err) {
      console.error('[AutoNewChat] Failed to process auto new chat:', err);
    }
  }, [isLoggedIn, createConversation, setActiveProjectId]);

  // ── Real-time postMessage listener from EricMEC ──
  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      const { type, project, workspace, prompt, token, agent_id, tunnel_url, hostname, pipeline } = event.data;
      
      if (
        type === 'ERIC_MEC_WORKSPACE' || 
        type === 'ERIC_MEC_PIPELINE' || 
        type === 'ERICODER_NEW_PROJECT' ||
        type === 'ERICODER_OPEN_PROJECT' ||
        type === 'PUSH_PIPELINE'
      ) {
        const targetWs = workspace || project;
        if (token) {
          localStorage.setItem('eric_mec_local_token', token);
        }
        if (agent_id || tunnel_url) {
          const activeAgent = {
            agent_id: agent_id || 'local-agent',
            hostname: hostname || 'Local PC',
            tunnel_url: tunnel_url || 'http://localhost:8080',
            agent_token: token || '',
            workspaces: targetWs ? [targetWs] : []
          };
          localStorage.setItem('eric_mec_active_agent', JSON.stringify(activeAgent));
        }
        if (targetWs) {
          const existingStr = localStorage.getItem('eric_mec_workspaces');
          let workspaces: string[] = [];
          try { if (existingStr) workspaces = JSON.parse(existingStr); } catch (e) {}
          if (Array.isArray(workspaces) && !workspaces.includes(targetWs)) {
            workspaces.push(targetWs);
            localStorage.setItem('eric_mec_workspaces', JSON.stringify(workspaces));
          }
          setLocalWorkspaces(workspaces);
          setActiveProjectId(targetWs);
          
          const autoPrompt = prompt || (pipeline ? `Thực thi pipeline: ${pipeline}` : '');
          createConversation(targetWs, autoPrompt);
          if (autoPrompt) {
            sessionStorage.setItem('ericoder_initial_prompt', autoPrompt);
          }
          window.dispatchEvent(new Event('storage'));
        }
      }
    };

    window.addEventListener('message', handlePostMessage);
    return () => window.removeEventListener('message', handlePostMessage);
  }, [createConversation, setActiveProjectId]);

  useEffect(() => {
    if (!isLoggedIn || !shouldSync || !activeProjectId) return;

    const timer = setTimeout(() => {
      console.log('[useChatHistory] Saving conversations for project', activeProjectId);
      
      const isLocal = localWorkspaces.includes(activeProjectId) || activeProjectId.includes('\\') || activeProjectId.includes('/');
      
      if (isLocal) {
         const dataStr = JSON.stringify({ conversations });
         
         executeLocalTool('write_to_file', {
            TargetFile: `${activeProjectId}\\.ericoder\\chat_history.json`,
            CodeContent: dataStr,
            Overwrite: true
         }).then(() => {
            setShouldSync(false);
         }).catch(err => console.error('[useChatHistory] Local save failed:', err));
      } else {
         api.saveProjectChatHistory(activeProjectId, conversations)
           .then(() => setShouldSync(false))
           .catch(err => console.error('[useChatHistory] Cloud save failed:', err));
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [conversations, isLoggedIn, shouldSync, activeProjectId, localWorkspaces]);



  const deleteConversationsByProject = useCallback((projectId: string) => {
    const isLocal = localWorkspaces.includes(projectId) || projectId.includes('\\') || projectId.includes('/');
    if (isLocal) {
        const safeProjId = projectId.replace(/'/g, "''"); // Escape single quotes for PowerShell
        executeLocalTool('run_command', { 
            CommandLine: `powershell -NoProfile -Command "Remove-Item '${safeProjId}\.ericoder\chat_history.json' -ErrorAction SilentlyContinue"` 
        });
    } else {
        api.saveProjectChatHistory(projectId, []);
    }

    if (activeProjectId === projectId) {
        setConversations([]);
        setActiveId(null);
    }
  }, [activeProjectId, localWorkspaces]);

  return (
    <ChatHistoryContext.Provider value={{
      activeProjectId,
      setActiveProjectId,
      conversations,
      activeId,
      projects,
      localWorkspaces,
      loadingProjects,
      createConversation,
      deleteConversation,
      updateMessages,
      setActiveId,
      loadProjects,
      deleteConversationsByProject
    }}>
      {children}
    </ChatHistoryContext.Provider>
  );
}

export const useChatHistory = () => {
  const ctx = useContext(ChatHistoryContext);
  if (!ctx) throw new Error('useChatHistory must be used within ChatHistoryProvider');
  return ctx;
};
